/*
 * Copyright 2026 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * External dependencies
 */
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { memo, useCallback, useEffect, useMemo } from '@web-stories-wp/react';
import { __ } from '@web-stories-wp/i18n';

/**
 * Internal dependencies
 */
import { useLayout, useStory } from '../../app';
import { STABLE_ARRAY } from '../../constants';
import {
  cancelDynamicCropRecording,
  useDynamicCropRecorderState,
} from '../../elements/video/dynamicCropRecorder';
import DynamicCropRecorderDisplay from '../../elements/video/dynamicCropRecorderDisplay';
import { getDynamicCropTargetAspectRatio } from '../../elements/video/dynamicCropTargetAspectRatio';
import { Layer, Z_INDEX } from './layout';
import { useStudioContext } from '@src/hooks/studioContext';

export const RECORDER_HORIZONTAL_INSET = 32;
const RECORDER_BACKGROUND_COLOR = '#050814';

const RecorderSlot = styled.div`
  grid-area: p;
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  box-sizing: border-box;
  padding: 0 ${RECORDER_HORIZONTAL_INSET}px;
  background: ${({ $isTransparent, theme }) =>
    $isTransparent
      ? 'transparent'
      : theme?.colors?.bg?.primary || RECORDER_BACKGROUND_COLOR};
  pointer-events: initial;
`;

const RecorderSurface = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  background: ${({ $isTransparent, theme }) =>
    $isTransparent
      ? 'transparent'
      : theme?.colors?.bg?.primary || RECORDER_BACKGROUND_COLOR};
  pointer-events: initial;
`;

export function VideoModeLayer({
  ariaLabel,
  children,
  isTransparent = false,
  overlayTestId,
  testId,
}) {
  const stopVideoModeEvent = useCallback((event) => {
    event.stopPropagation();
  }, []);

  return (
    <Layer
      aria-label={ariaLabel}
      data-testid={testId}
      pointerEvents="none"
      zIndex={Z_INDEX.EDIT + 1}
    >
      <RecorderSlot
        $isTransparent={isTransparent}
        data-testid={overlayTestId}
        onClick={stopVideoModeEvent}
        onMouseDown={stopVideoModeEvent}
        onPointerDown={stopVideoModeEvent}
      >
        <RecorderSurface $isTransparent={isTransparent}>
          {children}
        </RecorderSurface>
      </RecorderSlot>
    </Layer>
  );
}

VideoModeLayer.propTypes = {
  ariaLabel: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
  isTransparent: PropTypes.bool,
  overlayTestId: PropTypes.string,
  testId: PropTypes.string.isRequired,
};

function DynamicCropRecorderLayer() {
  const recorderState = useDynamicCropRecorderState();
  const studioContext = useStudioContext();
  const { currentPageElements } = useStory(({ state }) => ({
    currentPageElements: state.currentPage?.elements || STABLE_ARRAY,
  }));
  const { viewportWidth, viewportHeight } = useLayout(
    ({ state: { viewportWidth, viewportHeight } }) => ({
      viewportWidth,
      viewportHeight,
    })
  );
  const activeElement = useMemo(
    () =>
      currentPageElements.find(
        ({ id, isBackground, type }) =>
          id === recorderState.elementId && type === 'video' && isBackground
      ),
    [currentPageElements, recorderState.elementId]
  );
  useEffect(() => {
    if (recorderState.elementId && !activeElement) {
      cancelDynamicCropRecording();
    }
  }, [activeElement, recorderState.elementId]);

  useEffect(() => {
    if (!activeElement) {
      return undefined;
    }

    const handleKeyDown = (event) => {
      if (event.key !== 'Escape' && event.key !== 'Esc') {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      cancelDynamicCropRecording();
    };

    document.addEventListener('keydown', handleKeyDown, true);
    return () => {
      document.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [activeElement]);

  if (!activeElement) {
    return null;
  }

  const recorderWidth = Math.max(
    viewportWidth - RECORDER_HORIZONTAL_INSET * 2,
    0
  );
  const targetAspectRatio = getDynamicCropTargetAspectRatio({
    studioContext,
    fallbackWidth: activeElement.width ?? activeElement.resource?.width,
    fallbackHeight: activeElement.height ?? activeElement.resource?.height,
  });

  return (
    <VideoModeLayer
      ariaLabel={__('Dynamic crop recorder layer', 'web-stories')}
      testId="dynamicCropRecorderLayer"
      overlayTestId="dynamicCropRecorderOverlay"
    >
      <DynamicCropRecorderDisplay
        element={activeElement}
        width={recorderWidth}
        height={viewportHeight}
        targetAspectRatio={targetAspectRatio}
      />
    </VideoModeLayer>
  );
}

export default memo(DynamicCropRecorderLayer);
